import { useState } from 'react'
import { useDefinirPrazoDeRelatorio, useTiposDeRelatorio } from '../../../api/queries/relatorios'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { Cartao } from '../../../components/ListaDeDados'
import { CampoDeTexto } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'
import type { TipoDeRelatorio } from '../../../types/relatorios'

/**
 * O dia até o qual cada relatório mensal da contratada é entrega em dia.
 *
 * **Este bloco existe porque o ato não tinha tela.** O endpoint que fixa o dia limite estava
 * pronto e sem chamador: "não entregue" nunca virava "atrasado", em mês nenhum — não porque a
 * fiscalização tenha decidido não cobrar, mas porque ninguém conseguia fixar o dia.
 *
 * **E o texto é metade da entrega.** Fixar o dia não é configurar o sistema: é criar obrigação que
 * o contrato não criou, contra quem não a assumiu. O contrato diz "mensalmente" e nunca escreve o
 * dia; quem escreve é a CONTRATANTE, aqui. Por isso o aviso vem antes dos campos, e não depois.
 *
 * @returns o cartão dos prazos
 */
export function PrazosDosRelatorios() {
  const { data: tipos, isPending, isError, error } = useTiposDeRelatorio()
  const t = TEXTOS.referencias.prazos

  return (
    <Cartao titulo={t.titulo} descricao={t.ajuda}>
      <p className="mb-3 max-w-prose text-sm text-slate-600">{t.oQueOContratoDiz}</p>
      <p className="mb-3 max-w-prose rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
        {t.oQueFixarCria} {t.efeitoRetroativo}
      </p>

      {isPending && <Esqueleto linhas={4} />}
      {isError && <AvisoDeErro erro={error} titulo={TEXTOS.referencias.naoCarregou} />}

      {tipos && (
        <ul className="divide-y divide-slate-100">
          {tipos.map((tipo) => (
            <LinhaDoPrazo key={tipo.codigo} tipo={tipo} />
          ))}
        </ul>
      )}
    </Cartao>
  )
}

/** Um relatório cobrado: o que ele é, de onde o contrato o cobra, e o dia limite dele. */
function LinhaDoPrazo({ tipo }: { tipo: TipoDeRelatorio }) {
  const t = TEXTOS.referencias.prazos
  const definir = useDefinirPrazoDeRelatorio()
  // O campo nasce com o dia que está valendo. `diaLimite` chega AUSENTE quando não há prazo — o
  // backend omite campo nulo —, e por isso o teste é de valor falso, e não `=== null`.
  const [dia, setDia] = useState(tipo.diaLimite ? String(tipo.diaLimite) : '')

  const numero = Number(dia)
  const valido = dia.trim().length > 0 && Number.isInteger(numero) && numero >= 1 && numero <= 31

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-slate-900">{tipo.rotulo}</span>
          <span className="mt-0.5 block text-xs text-slate-500">{tipo.fonteDoTr}</span>
        </span>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
            tipo.diaLimite
              ? 'bg-amber-50 text-amber-900 ring-amber-200'
              : 'bg-slate-100 text-slate-700 ring-slate-200'
          }`}
        >
          {tipo.diaLimite ? t.comPrazo(tipo.diaLimite) : t.semPrazo}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <CampoDeTexto
            rotulo={t.dia}
            ajuda={t.diaAjuda}
            type="number"
            min="1"
            max="31"
            inputMode="numeric"
            value={dia}
            onChange={(e) => setDia(e.target.value)}
          />
        </div>
        <Botao
          aparencia="primario"
          onClick={() => definir.mutate({ tipo: tipo.codigo, diaLimite: numero })}
          disabled={!valido || definir.isPending}
        >
          {t.fixar}
        </Botao>
        {tipo.diaLimite ? (
          <Botao
            onClick={() => {
              setDia('')
              definir.mutate({ tipo: tipo.codigo, diaLimite: null })
            }}
            disabled={definir.isPending}
          >
            {t.tirar}
          </Botao>
        ) : null}
      </div>

      {definir.isError && <AvisoDeErro erro={definir.error} titulo={t.naoSalvou} />}
    </li>
  )
}
